import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import CtaBand from '@/components/feature/CtaBand';
import SiteLayout from '@/components/feature/SiteLayout';
import { useSeo } from '@/hooks/useSeo';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function Archives() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') ?? 'all';
  const [keyword, setKeyword] = useState('');

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return articles.filter((article) => {
      const matchCat = activeCategory === 'all' || article.category === activeCategory;
      const matchKw =
        !kw ||
        article.title.toLowerCase().includes(kw) ||
        article.excerpt.toLowerCase().includes(kw) ||
        article.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCat && matchKw;
    });
  }, [activeCategory, keyword]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const listTitle = currentCategory ? `${currentCategory.name} · 内幕档案` : '全部内幕档案';

  useSeo({
    title: `${listTitle}｜缅北黑料 · 反诈内幕档案库`,
    description:
      '浏览缅北黑料全部内幕档案，涵盖诈骗园区运作、电诈窝点套路、受害者亲历讲述与出境防骗指南，支持按分类筛选与关键词搜索。',
    keywords: '缅北黑料档案,缅北黑料诈骗园区,电诈窝点内幕,受害者经历,防骗指南',
    path: activeCategory === 'all' ? '/archives' : `/archives?category=${activeCategory}`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: listTitle,
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://readdy.ai/' },
          { '@type': 'ListItem', position: 2, name: '内幕档案' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: filtered.map((a, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: a.title,
        })),
      },
    ],
  });

  const handleCategory = (slug: string) => {
    if (slug === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ category: slug });
    }
  };

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-content px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={currentCategory ? [{ label: '内幕档案', to: '/archives' }, { label: currentCategory.name }] : [{ label: '内幕档案' }]} />
          <h1 className="mt-5 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
            {currentCategory ? currentCategory.name : '内幕档案'}
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {currentCategory
              ? currentCategory.desc
              : '按主题整理的跨境诈骗曝光内容。每个分类下都尽量保留可核实的线索与识别要点，方便你转发给需要的人。'}
          </p>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-content px-4 py-10 md:px-6 md:py-12">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => handleCategory('all')}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  activeCategory === 'all'
                    ? 'bg-foreground-950 text-background-50'
                    : 'border border-background-200 bg-background-50 text-foreground-700 hover:bg-background-100'
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  type="button"
                  onClick={() => handleCategory(cat.slug)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    activeCategory === cat.slug
                      ? 'bg-foreground-950 text-background-50'
                      : 'border border-background-200 bg-background-50 text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="relative w-full lg:w-72">
              <i
                className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400"
                aria-hidden="true"
              ></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索关键词，如 杀猪盘"
                aria-label="搜索档案"
                className="w-full rounded-md border border-background-200 bg-background-50 py-2.5 pl-10 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>
          </div>

          <p className="mt-6 text-sm text-foreground-500">
            共找到 <strong className="font-semibold text-foreground-900">{filtered.length}</strong> 篇档案
          </p>

          {filtered.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          ) : (
            <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-search-eye-line text-2xl" aria-hidden="true"></i>
              </span>
              <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">没有找到匹配的档案</h2>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                换一个关键词试试，或者回到全部档案按分类浏览。
              </p>
              <button
                type="button"
                onClick={() => {
                  setKeyword('');
                  handleCategory('all');
                }}
                className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-foreground-950 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-foreground-800"
              >
                重置筛选
              </button>
              <Link to="/faq" className="mt-3 text-sm text-primary-700 hover:text-primary-800">
                浏览常见问题 →
              </Link>
            </div>
          )}
        </div>
      </section>

      <CtaBand />
    </SiteLayout>
  );
}